import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import RoleGate from './auth/RoleGate';
import Login from './pages/auth/Login';
import WikiLayout from './components/wiki/WikiLayout';
import WikiDashboard from './pages/wiki/WikiDashboard';
import WikiDiscovery from './pages/wiki/WikiDiscovery';
import WikiEntry from './pages/wiki/WikiEntry';
import WikiEntryEdit from './pages/wiki/WikiEntryEdit';
import WikiEdit from './pages/wiki/WikiEdit';
import WikiHistory from './pages/wiki/WikiHistory';
import WikiDiff from './pages/wiki/WikiDiff';
import MyDrafts from './pages/wiki/MyDrafts';
import WikiBundles, { WikiBundleDetail, WikiBundleEdit } from './pages/wiki/WikiBundles';
import AdminStats from './pages/admin/AdminStats';
import AdminUsers from './pages/admin/AdminUsers';
import Dashboard from './pages/Dashboard';
import CaseDetail from './pages/CaseDetail';
import NewCase from './pages/NewCase';
import Lexis from './pages/Lexis';
import LexisEntry from './pages/LexisEntry';
import LexisVendors from './pages/LexisVendors';
import LexisScan from './pages/LexisScan';
import LexisReport from './pages/LexisReport';
import LexisUploader from './pages/LexisUploader';
import TenderIntel from './pages/TenderIntel';

export default function App() {
    return (
        <AuthProvider>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />

                {/* Wiki routes — protected, with wiki layout */}
                <Route
                    path="/wiki"
                    element={
                        <ProtectedRoute>
                            <WikiLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<WikiDashboard />} />
                    <Route path="discover" element={<WikiDiscovery />} />
                    <Route path="entry/:slug" element={<WikiEntry />} />
                    {/* Editor routes */}
                    <Route path="edit/:id" element={<WikiEntryEdit />} />
                    <Route path="edit/new" element={<WikiEntryEdit />} />
                    <Route path=":id/edit" element={
                        <ProtectedRoute>
                            <RoleGate roles={['admin', 'editor']}>
                                <WikiEdit />
                            </RoleGate>
                        </ProtectedRoute>
                    } />
                    <Route path="entry/:id/history" element={<WikiHistory />} />
                    <Route path="entry/:id/diff" element={<WikiDiff />} />
                    <Route path="drafts" element={<MyDrafts />} />
                    {/* Discovery & Bundles */}
                    <Route path="bundles" element={<WikiBundles />} />
                    <Route path="bundles/:slug" element={<WikiBundleDetail />} />
                    <Route path="bundles/new" element={<WikiBundleEdit />} />
                    <Route path="bundles/:id/edit" element={<WikiBundleEdit />} />
                    {/* Admin routes */}
                    <Route path="admin/stats" element={<AdminStats />} />
                    <Route path="admin/users" element={<AdminUsers />} />
                </Route>

                {/* Legacy app routes — preserved */}
                <Route path="/*" element={<LegacyApp />} />
            </Routes>
        </AuthProvider>
    );
}

function LegacyApp() {
    const location = useLocation();

    return (
        <div className="app">
            <header className="header">
                <h1>&#128269; LESIA</h1>
                <nav style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <Link to="/" className={location.pathname === '/' ? 'active' : ''}>Dashboard</Link>
                    <Link to="/cases/new" className={location.pathname === '/cases/new' ? 'active' : ''}>New Case</Link>
                    <Link to="/lexis" className={location.pathname.startsWith('/lexis') ? 'active' : ''}>&#128214; LEXIS KB</Link>
                    <Link to="/intel" className={location.pathname.startsWith('/intel') ? 'active' : ''}>&#128278; Tender Intel</Link>
                    <Link to="/wiki" className={location.pathname.startsWith('/wiki') ? 'active' : ''}>&#128218; Wiki</Link>
                </nav>
            </header>
            <div style={{ background: 'rgba(15,23,42,0.4)', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
                <nav style={{ maxWidth: '1400px', margin: '0 auto', padding: '8px 20px', display: 'flex', gap: '8px', fontSize: '13px' }}>
                    <Link to="/lexis" style={subNavLinkStyle('/lexis', location)}>&#128196; All Entries</Link>
                    <Link to="/lexis/vendors" style={subNavLinkStyle('/lexis/vendors', location)}>&#127970; Vendors</Link>
                    <Link to="/lexis/scan" style={subNavLinkStyle('/lexis/scan', location)}>&#128193; Scan</Link>
                    <Link to="/lexis/report" style={subNavLinkStyle('/lexis/report', location)}>&#128202; Portfolio</Link>
                    <Link to="/lexis/upload" style={subNavLinkStyle('/lexis/upload', location)}>&#128228; Upload Documents</Link>
                </nav>
            </div>
            <main className="main">
                <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/cases/new" element={<NewCase />} />
                    <Route path="/cases/:id" element={<CaseDetail />} />
                    <Route path="/lexis" element={<Lexis />} />
                    <Route path="/lexis/:id" element={<LexisEntry />} />
                    <Route path="/lexis/vendors" element={<LexisVendors />} />
                    <Route path="/lexis/scan" element={<LexisScan />} />
                    <Route path="/lexis/report" element={<LexisReport />} />
                    <Route path="/lexis/upload" element={<LexisUploader />} />
                    <Route path="/intel" element={<TenderIntel />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>
        </div>
    );
}

function subNavLinkStyle(path, location) {
    const active = location.pathname === path;
    return {
        color: active ? '#d4af37' : '#94a3b8',
        textAlign: 'center',
        padding: '6px 14px',
        borderRadius: '6px',
        background: active ? 'rgba(212,175,55,0.1)' : 'transparent',
        textDecoration: 'none',
    };
}